import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | failed | timeout
  const [info, setInfo] = useState(null);

  const check = useCallback(async () => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      setInfo(data);
      if (data.payment_status === "paid") setStatus("paid");
      return data.payment_status;
    } catch {
      // ignore transient
    }
  }, [sessionId]);

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    const run = async () => {
      while (!cancelled && attempts < 20) {
        const s = await check();
        if (s === "paid") return;
        attempts += 1;
        await new Promise((r) => setTimeout(r, 2000));
      }
      if (!cancelled) setStatus((s) => (s === "paid" ? s : "timeout"));
    };
    run();
    return () => { cancelled = true; };
  }, [check]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-16 bg-grain">
      <Card className="max-w-lg w-full p-8 text-center">
        {status === "checking" && (
          <>
            <Loader2 className="h-10 w-10 mx-auto animate-spin text-primary" />
            <h1 className="mt-4 font-heading text-2xl font-bold">Confirming your payment…</h1>
            <p className="text-muted-foreground mt-2">
              Hang tight — Stripe is sending us the confirmation.
            </p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="h-14 w-14 mx-auto rounded-full bg-emerald-100 dark:bg-emerald-950/60 flex items-center justify-center">
              <CheckCircle2 className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />
            </div>
            <h1 className="mt-5 font-heading text-3xl font-bold" data-testid="payment-success-heading">
              Session booked!
            </h1>
            <p className="text-muted-foreground mt-2">
              We've emailed your tutor — they'll confirm or suggest a new time shortly. If they
              decline, your payment is refunded automatically.
            </p>
            {info?.amount_cents != null && (
              <p className="mt-4 text-sm">
                <span className="text-muted-foreground">Charged: </span>
                <span className="font-semibold">${(info.amount_cents / 100).toFixed(2)} USD</span>
              </p>
            )}
            <div className="mt-6 flex gap-3 justify-center">
              <Button asChild variant="outline" className="rounded-full">
                <Link to="/">Back home</Link>
              </Button>
              <Button asChild className="rounded-full">
                <Link to="/tutors">Browse more tutors</Link>
              </Button>
            </div>
          </>
        )}
        {status === "timeout" && (
          <>
            <h1 className="font-heading text-2xl font-bold">Still processing…</h1>
            <p className="text-muted-foreground mt-2">
              Stripe is taking a bit longer than usual. Refresh in a minute — your receipt will
              arrive by email either way.
            </p>
          </>
        )}
        {status === "failed" && (
          <>
            <XCircle className="h-10 w-10 mx-auto text-rose-500" />
            <h1 className="mt-4 font-heading text-2xl font-bold">Missing session reference</h1>
            <p className="text-muted-foreground mt-2">
              We couldn't find your checkout. Please start a new booking.
            </p>
          </>
        )}
      </Card>
    </div>
  );
}
